import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { CUSTOMAPIService } from 'app/custom-api.service';
import { UtilsService } from 'app/shared/utils.service';
import { BNB_SCORE_BASE, DEFAULT_SCORE_BASE } from './_constant/scoreBase.constant';

export interface ScoreBase {
  [key: string]: number;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreService {
  private static entityBaseCache: ScoreBase = {}; //* Cache for entity score bases

  private readonly BNB_SCORE_BASE: number = BNB_SCORE_BASE; //* Fixed base for BNB instance
  private readonly DEFAULT_SCORE_BASE: number = DEFAULT_SCORE_BASE; //* Default base if none found

  private currentScoreBase: number = this.DEFAULT_SCORE_BASE; //* Current score base for the instance

  constructor(
    private customApi: CUSTOMAPIService,
    private router: Router
  ) {}

  public initScoreService(): void {
    this.listenToRouteParams();
  }

  //* Listen to route parameter changes to update score base accordingly
  private listenToRouteParams(): any {
    this.router.events
      .pipe(
        // 1. Only run this when navigation finishes
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        // 2. Traverse the route tree to find the deepest child
        let currentRoute = this.router.routerState.root;
        let params: any = {};
        while (currentRoute) {
          params = { ...params, ...currentRoute.snapshot.params };
          currentRoute = currentRoute.firstChild;
        }

        //* Initialize score base for the new entity ID
        if (params.entityId) {
          this.initializeEntityScoreBase(params.entityId);
        }
      });
  }

  //* Fetch root entity from database
  private async getRootEntityFromDb(entityId: string): Promise<any> {
    const entity = await this.customApi.GetEntityScoreBase(entityId);
    return entity || null;
  }

  //* Initialize score base for a given entity ID with caching
  public async initializeEntityScoreBase(entityId: string): Promise<void> {
    //* Check cache first
    if (ScoreService.entityBaseCache[entityId]) {
      this.updateScoreBase(ScoreService.entityBaseCache[entityId]);
      return;
    }
    //* Fetch from database if not in cache
    const entity = await this.getRootEntityFromDb(entityId);
    const scoreBase = entity?.scoringBase ?? this.DEFAULT_SCORE_BASE;
    this.updateScoreBase(scoreBase);
    ScoreService.entityBaseCache[entityId] = scoreBase; //* Cache the fetched base
  }

  public updateEntityScoreBaseInCache(entityId: string, newBase: number, initialize: boolean = false): void {
    ScoreService.entityBaseCache[entityId] = newBase;
    if (initialize) {
      this.updateScoreBase(newBase);
    }
  }

  public getEntityScoreBaseFromCache(entityId: string): number | null {
    return ScoreService.entityBaseCache[entityId] || DEFAULT_SCORE_BASE;
  }

  //* Getter for current score base
  public get activeScoreBase(): number {
    if (UtilsService.isBnB) {
      return this.BNB_SCORE_BASE;
    }
    return this.currentScoreBase || this.DEFAULT_SCORE_BASE;
  }

  //* Update current score base
  private updateScoreBase(newBase: number): void {
    this.currentScoreBase = newBase;
  }

  //* return grid lines based on active score base
  public getGridLines(): number[] {
    const base = this.activeScoreBase;
    const gridLines: number[] = [];
    for (let i = base; i >= 0; i--) {
      gridLines.push(i);
    }
    return gridLines;
  }

  public getScaledScore(score: any, isThirdParty: boolean = false, customBase: number = null): any {
    const fallbackBase = isThirdParty ? this.DEFAULT_SCORE_BASE : (this.activeScoreBase ?? this.DEFAULT_SCORE_BASE);
    const currentBase = customBase ?? fallbackBase;

    if (!UtilsService.isDefined(score)) {
      return (0.0).toFixed(1);
    }

    const numb = parseFloat(score);
    const isString = typeof score === 'string';

    const convertedScore = (numb / 10) * currentBase;

    // Format result based on input type and value
    if (isString) {
      return convertedScore.toFixed(1);
    }

    if (convertedScore === 0) {
      return (0.0).toFixed(1);
    }

    return parseFloat(convertedScore.toFixed(1));
  }
}
